import { Alert, AlertTitle, Button, Stack } from '@mui/material';
import { map } from 'lodash';
import React from 'react';

import {
  SpotDetectionActionSuggestion,
  SpotDetectionActionUpdate,
  StainSpotDetectionActionSuggestions,
  StainSpotDetectionActionUpdates,
} from './spotDetectionActionSuggestions';

interface SpotDetectionActionSuggestionsBannerProps {
  suggestionsByStain: StainSpotDetectionActionSuggestions[];
  onAddStainActions: (stain: string, suggestions: SpotDetectionActionSuggestion[]) => void;
  updatesByStain?: StainSpotDetectionActionUpdates[];
  onUpdateStainActions?: (stain: string, updates: SpotDetectionActionUpdate[]) => void;
}

const describeSuggestion = ({ actionLabel, isFeature }: SpotDetectionActionSuggestion): string =>
  `${actionLabel} (${isFeature ? 'Features' : 'Post Processing Actions'})`;

export const SpotDetectionActionSuggestionsBanner: React.FC<SpotDetectionActionSuggestionsBannerProps> = ({
  suggestionsByStain,
  onAddStainActions,
  updatesByStain = [],
  onUpdateStainActions,
}) => {
  const hasUpdates = onUpdateStainActions && updatesByStain.length > 0;
  if (suggestionsByStain.length === 0 && !hasUpdates) {
    return null;
  }

  return (
    <Stack spacing={1} sx={{ mb: 2 }}>
      {map(suggestionsByStain, ({ stain, stainDisplayName, suggestions }) => (
        <Alert
          key={stain}
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={() => onAddStainActions(stain, suggestions)}>
              Add for {stainDisplayName}
            </Button>
          }
        >
          <AlertTitle>Recommended actions for {stainDisplayName}</AlertTitle>
          Configured H-score bins for {stainDisplayName} are not usable until you add:{' '}
          {map(suggestions, describeSuggestion).join(', ')}.
        </Alert>
      ))}
      {onUpdateStainActions &&
        map(updatesByStain, ({ stain, stainDisplayName, updates }) => (
          <Alert
            key={stain}
            severity="warning"
            action={
              <Button color="inherit" size="small" onClick={() => onUpdateStainActions(stain, updates)}>
                Update for {stainDisplayName}
              </Button>
            }
          >
            <AlertTitle>H-score bins changed for {stainDisplayName}</AlertTitle>
            {map(updates, 'actionLabel').join(', ')} no longer match the current H-score bins. Updating replaces their
            existing configuration with values derived from the current bins.
          </Alert>
        ))}
    </Stack>
  );
};
